import { FileMediaLinks } from 'src/api/common';
import api from './baseApi';

export enum TribeAccountType {
  FREE = 'FREE', // free for max 2 members / leaders
  AUCTION = 'AUCTION', // member pays
  FIXED = 'FIXED', // leader pays
  DEMO = 'DEMO', // DUNBAR CIRCLE -- free
}

export enum MemberRole {
  Leader = 'LEADER',
  CoLeader = 'COLEADER',
  Member = 'MEMBER',
}

export enum TribeMemberBillingStatus {
  Pending = 'PENDING',
  UpToDate = 'UPTODATE',
  Behind = 'BEHIND',
  Failed = 'FAILED',
}

/**
 * Optional per-circle branding (presentation only). Absent on every circle
 * that has not been branded; clients must fall back to the Dunbar defaults.
 */
export type CircleBrandingButtonShape = 'rounded' | 'pill';

export interface CircleBranding {
  display_name?: string;
  logo_url?: string;
  logo_dark_url?: string;
  /** Header zone: top bar, circle header and tab bar. */
  primary_color?: string;
  /** Text and icons on top of primary_color. */
  on_primary_color?: string;
  /** Buttons, links and the active tab (falls back to primary_color). */
  secondary_color?: string;
  /** Page behind the circle content. */
  background_color?: string;
  /** Body text on top of background_color. */
  text_color?: string;
  font_family?: string;
  heading_font_family?: string;
  heading_uppercase?: boolean;
  button_shape?: CircleBrandingButtonShape;
  /** Also color the app navigation while the circle is open (default: no). */
  brand_navigation?: boolean;
}

/**
 * Optional links from a circle to outside systems. `review360` connects the
 * circle's "360" tab to one 360review team account (its owner's email).
 * Absent on every circle that has not been connected.
 */
export interface CircleIntegrations {
  review360?: { account_owner_email: string };
}

export interface Circle {
  _id: string;
  createdAt: string;
  updatedAt: string;
  leader_name: string;
  leader_id: string;
  leader_avatar_url: string;
  tribe_name: string;
  tribe_avatar_id: string;
  tribe_avatar: FileMediaLinks;
  tribe_avatar_url?: string;
  price: number;
  member_count_max: number;
  description: string;
  welcome_video_id: string;
  welcome_video: FileMediaLinks;
  status: string;
  special_code?: string;
  /** Six characters a LONELINESS Circle's members join by; absent elsewhere. */
  join_code?: string;
  account_type?: TribeAccountType;
  billing_status?: string;
  payment_link?: string;
  branding?: CircleBranding;
  integrations?: CircleIntegrations;
}

export enum TribeMemberStatus {
  PROCESSING = 'PROCESSING',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  APPROVED = 'APPROVED', // TODO: Deprecate APPROVED once MobileApp is updated to v1.46.0
  ACTIVE = 'ACTIVE',
  READONLY = 'READONLY',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED',
}

export interface TribeMembershipDetails {
  _id: string;
  leader_id: string;
  leader_name?: string;
  leader_avatar_url?: string;
  tribe_name?: string;
  status?: TribeMemberStatus;
  tribe_avatar_id?: string;
  tribe_avatar_url?: string;

  role?: MemberRole;
  join_status?: string;
  account_status?: TribeMemberBillingStatus;
  branding?: CircleBranding;

  user: {
    _id: string;
    full_name: string;
    avatar_id: string;
    avatar_url: string;
    bio: string;
    avatar: FileMediaLinks;
    phone_number?: string;
    email?: string;
    firm?: string | null;
    contactShareConsent?: 'approved' | 'declined';
  };
}

export type ContactShareConsent = 'approved' | 'declined';

export type CircleContactUser = {
  _id: string;
  full_name: string;
  avatar_id?: string;
  avatar_url?: string;
  avatar?: FileMediaLinks;
  bio?: string;
  contactShareConsent: ContactShareConsent;
  phone_number?: string;
  email?: string;
  firm?: string | null;
  notes?: string | null;
};

export type MemberInvoiceDetails = {
  firm_name: string | null;
  org_nr: string | null;
  email: string | null;
  reference: string | null;
  wants_ehf: unknown;
};

export type MemberRegistrationDetails = {
  first_name: string | null;
  surname: string | null;
  job_title: string | null;
  industry: string | null;
  leadership_level: string | null;
  gender: string | null;
  invoice: MemberInvoiceDetails;
  allergies: string | null;
  additional_comments: string | null;
  wants_hotel_room: unknown;
  consents: {
    contactShare: ContactShareConsent;
    photos: unknown;
    marketing: unknown;
  };
  answers: Record<string, unknown>;
};

export type MemberContactDetailsResponse = {
  user: CircleContactUser;
};

export type MemberLeaderDetailsResponse = {
  user: CircleContactUser;
  registration: MemberRegistrationDetails | null;
};

export interface FutureMembershipDetails {
  _id: string;
  tribe_id: string;
  role?: MemberRole;
  phone_number: string;
  email: string;
}

export interface UploadResults {
  added: number;
  existing: number;
  failed: number;
  invalidUsers?: { email: string; phone_number: string }[];
  message?: string;
  errors?: Array<{
    user: {
      email?: string;
      phone_number?: string;
    };
    error: string;
  }>;
}

export const circleApi = {
  createCircle: async (circleNewData: any): Promise<Circle> => {
    const axiosResponse = await api.post(`tribes`, circleNewData);
    return axiosResponse.data.result;
  },

  editCircle: async (
    circleNewData: Record<string, any>,
    id: string
  ): Promise<Circle> => {
    const axiosResponse = await api.patch(`tribes/${id}`, circleNewData);
    return axiosResponse.data.result;
  },

  editUserCount: async (
    id: string,
    member_count_max: number
  ): Promise<Circle> => {
    const axiosResponse = await api.patch(`tribes/${id}`, { member_count_max });
    return axiosResponse.data.result;
  },

  getCircle: async (id: string): Promise<Circle> => {
    const axiosResponse = await api.get(`tribes/${id}`);
    return axiosResponse.data.result;
  },

  getVideoId: async (id: string): Promise<string> => {
    const axiosResponse = await api.get(`files/${id}`);
    const videoId = axiosResponse.data.result?._id;
    return videoId;
  },

  inviteMemberByPhone: async (
    tribe_id: string,
    role: string,
    phone_number: string
  ): Promise<TribeMembershipDetails | FutureMembershipDetails> => {
    const axiosResponse = await api.post(`tribes/${tribe_id}/members/add`, {
      role,
      phone_number,
    });
    return axiosResponse.data.result;
  },
  inviteMemberByEmail: async (
    tribe_id: string,
    role: string,
    email: string
  ): Promise<TribeMembershipDetails | FutureMembershipDetails> => {
    const axiosResponse = await api.post(`tribes/${tribe_id}/members/add`, {
      role,
      email,
    });
    return axiosResponse.data.result;
  },

  bulkAddMembers: async (
    tribeId: string,
    data: { csvData: string }
  ): Promise<UploadResults> => {
    const axiosResponse = await api.post(
      `tribes/${tribeId}/members/bulk-add`,
      data
    );
    return axiosResponse.data.result;
  },

  deleteMember: async (tribe_id: string, user_id: string): Promise<void> => {
    await api.delete(`tribes/${tribe_id}/members/${user_id}`);
  },

  leaveCircle: async (id: string): Promise<void> => {
    await api.put(`tribes/${id}/members/leave`);
  },

  /**
   * @description
   * Close the Circle, cancel the subscription, and mark as READ_ONLY.
   *
   * NOTE: Circles should not be permanently deleted by users so they
   * are only closed and marked as READ_ONLY.
   */
  closeCircle: async (id: string): Promise<void> => {
    await api.patch(`tribes/${id}/close`);
  },

  getMembersOfCircle: async (id: string): Promise<TribeMembershipDetails[]> => {
    const axiosResponse = await api.get(`tribes/${id}/membersV2`);
    return axiosResponse.data.result;
  },

  getCircleContacts: async (id: string): Promise<unknown[]> => {
    const axiosResponse = await api.get(`tribes/${id}/contacts`);
    return axiosResponse.data.result ?? [];
  },

  getCircleContact: async (
    tribeId: string,
    userId: string,
    signal?: AbortSignal
  ): Promise<MemberContactDetailsResponse> => {
    const axiosResponse = await api.get(
      `tribes/${encodeURIComponent(tribeId)}/contacts/${encodeURIComponent(userId)}`,
      { signal }
    );
    return axiosResponse.data.result;
  },

  getCircleMemberDetails: async (
    tribeId: string,
    userId: string,
    signal?: AbortSignal
  ): Promise<MemberLeaderDetailsResponse> => {
    const axiosResponse = await api.get(
      `tribes/${encodeURIComponent(tribeId)}/member-details/${encodeURIComponent(userId)}`,
      { signal }
    );
    return axiosResponse.data.result;
  },

  getCircleMemberDirectory: async (id: string): Promise<unknown[]> => {
    const axiosResponse = await api.get(`tribes/${id}/members`);
    return axiosResponse.data.result ?? [];
  },

  getFutureMembersOfCircle: async (
    id: string
  ): Promise<FutureMembershipDetails[]> => {
    const axiosResponse = await api.get(`tribes/${id}/future-members`);
    return axiosResponse.data.result;
  },
};
